import { useQuery, useMutation } from "@apollo/client";
import { useState, useMemo } from "react";
import {
  useQueryParam,
  withDefault,
  NumberParam,
  StringParam
} from "use-query-params";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import { TableControls } from "src/components/Table";
import {
  listSubaccounts,
  listSubaccounts_listSubaccounts
} from "src/graphql/generated/listSubaccounts";
import {
  subaccountCreate,
  subaccountCreateVariables
} from "src/graphql/generated/subaccountCreate";
import { CREATE_SUBACCOUNT, LIST_SUBACCOUNTS } from "src/graphql/subaccount";

import AddSubaccountDialog from "./AddSubaccountDialog";
import SubaccountTable from "./SubaccountTable";
import { SubaccountSort, SUBACCOUNT_SORTS } from "./constants";

type Subaccount = listSubaccounts_listSubaccounts;

function validateSort(sort: string): SubaccountSort {
  return (
    SUBACCOUNT_SORTS.find(s => s.key === sort)?.key ?? SUBACCOUNT_SORTS[0].key
  );
}

export default function Subaccounts(): JSX.Element {
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, SUBACCOUNT_SORTS[0].key)
  );

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const validatedSort = validateSort(sort);

  const { loading, error, data, refetch } =
    useQuery<listSubaccounts>(LIST_SUBACCOUNTS);

  const [addSubaccountDialogOpen, toggleAddSubaccountDialog] = useState(false);

  const [addSubaccount] = useMutation<
    subaccountCreate,
    subaccountCreateVariables
  >(CREATE_SUBACCOUNT);

  const filteredSubaccounts: Subaccount[] = useMemo(() => {
    const allSubaccounts = data?.listSubaccounts ?? [];

    // filter
    const filteredSubaccounts = search
      ? allSubaccounts.filter(s =>
          s.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
        )
      : allSubaccounts.slice(0);

    // sort
    return filteredSubaccounts.sort((a, b) => {
      switch (validatedSort) {
        case "created_at_asc":
          return a.createdAt - b.createdAt;
        case "created_at_desc":
          return b.createdAt - a.createdAt;
        case "name_asc":
          return (a.name || "").localeCompare(b.name || "");
        case "name_desc":
          return (b.name || "").localeCompare(a.name || "");
      }
    });
  }, [data, validatedSort, search]);

  const rows = filteredSubaccounts.slice(
    page * pageSize,
    (page + 1) * pageSize
  );

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  async function handleAddSubaccount(values: subaccountCreateVariables) {
    const { errors } = await addSubaccount({
      variables: values
    });
    if (errors) {
      throw errors;
    } else {
      await refetch();
      toggleAddSubaccountDialog(false);
    }
  }

  return (
    <>
      <TableControls
        addButtons={[
          {
            label: "Add Subaccount",
            onClick: () => toggleAddSubaccountDialog(true)
          }
        ]}
        sort={{
          value: sort,
          onChange: value => setSort(value as SubaccountSort),
          options: SUBACCOUNT_SORTS.map(sort => ({
            value: sort.key,
            label: sort.label
          }))
        }}
        search={{
          value: search,
          onChange: value => setSearch(value as string),
          placeholder: "Search for a subaccount..."
        }}
      />
      {loading ? (
        <LoadingIndicator />
      ) : (
        <SubaccountTable
          rows={rows}
          handleChangePage={setPage}
          handleChangeRowsPerPage={newRowsPerPage => {
            setPageSize(newRowsPerPage);
            setPage(0);
          }}
          rowsPerPage={pageSize}
          page={page}
          totalCount={filteredSubaccounts.length}
        />
      )}
      <AddSubaccountDialog
        open={addSubaccountDialogOpen}
        onClose={() => toggleAddSubaccountDialog(false)}
        onSubmit={handleAddSubaccount}
      />
    </>
  );
}
